/**
 * Quotation types, state machine, and DTOs.
 *
 * The quotation lifecycle:
 *   draft -> sent -> accepted | rejected
 *
 * State transitions are validated by ALLOWED_TRANSITIONS.
 * Timestamps (sent_at, accepted_at, rejected_at) are auto-set on transition.
 */

import type { QuotationStatus } from './enums.js'
import type { Client } from './clients.js'
import type { Account } from './accounts.js'

// ── State Machine ────────────────────────────────────────────────────────────

/**
 * Allowed status transitions for quotations.
 * Key = current status, Value = list of valid target statuses.
 * Terminal states (accepted, rejected) have no transitions.
 */
export const ALLOWED_TRANSITIONS: Record<QuotationStatus, readonly QuotationStatus[]> = {
  draft: ['sent'],
  sent: ['accepted', 'rejected'],
  accepted: [],
  rejected: [],
} as const

/**
 * Map from target status to the timestamp column that should be set.
 */
export const STATUS_TIMESTAMP_FIELD: Partial<Record<QuotationStatus, string>> = {
  sent: 'sent_at',
  accepted: 'accepted_at',
  rejected: 'rejected_at',
}

// ── DTOs ─────────────────────────────────────────────────────────────────────

export interface CreateQuotationDTO {
  clientName: string
  contactEmail: string
  contactPhone?: string
  totalCents: number
  discountCents?: number
  notes?: string
  validUntil?: string // ISO date string (YYYY-MM-DD)
}

export interface QuotationRow {
  id: string
  client_name: string
  contact_email: string
  contact_phone: string | null
  status: QuotationStatus
  total_cents: number
  discount_cents: number
  notes: string | null
  created_by_id: string
  client_id: string | null
  sent_at: string | null
  accepted_at: string | null
  rejected_at: string | null
  valid_until: string | null
  created_at: string
  updated_at: string
  // Joined fields (populated in detail/list queries)
  creator_name?: string
  creator_email?: string
}

export interface QuotationListFilters {
  status?: QuotationStatus
  search?: string
  limit?: number
  offset?: number
}

// ── Conversion ────────────────────────────────────────────────────────────────

/**
 * Result returned after converting an accepted quotation to a Client + Account.
 * Both records are created atomically in a single PostgreSQL transaction.
 */
export interface ConversionResult {
  client: Client
  account: Account
  contractPdfUrl?: string
}
